import React, { useEffect, useState } from "react";
import { Container, Card, Row, Col, Button, Form, Spinner, Alert } from "react-bootstrap";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { functions } from "../../../firebase/config";
import RavenHead from "../../../assets/smoke-raven-avatar.webp";
import onomasticonLogo from "../../../assets/logo-onomasticon.webp";

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [subscribed, setSubscribed] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionUpdating, setSubscriptionUpdating] = useState(false);
  const [manualResubscribeNeeded, setManualResubscribeNeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      try {
        const getSubscriptionStatus = httpsCallable(functions, "getSubscriptionStatus");
        const { data } = await getSubscriptionStatus();
        if (!cancelled) {
          // `null` means no sync has run for this account yet — treat as
          // subscribed, matching what the next sync will actually set.
          setSubscribed(data.subscribed === null ? true : data.subscribed);
        }
      } catch (error) {
        console.error("Failed to load subscription status:", error);
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    };

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubscriptionToggle = async (e) => {
    const next = e.target.checked;
    setSubscriptionUpdating(true);
    setManualResubscribeNeeded(false);

    try {
      const updateSubscription = httpsCallable(functions, "updateSubscription");
      const { data } = await updateSubscription({ subscribed: next });
      setSubscribed(data.subscribed);
      if (data.mailchimpAction === "manual_required") {
        setManualResubscribeNeeded(true);
      }
    } catch (error) {
      console.error("Failed to update subscription:", error);
    } finally {
      setSubscriptionUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  return (
    <Container className="mt-5">
      <Row>
        <Col md={8} className="mx-auto">
          <Card>
            <Card.Header as="h5" className="bg-success text-white">
              Member Dashboard
            </Card.Header>
            <Card.Body>
              <h4>Welcome, {currentUser?.displayName || "User"}!</h4>

              <div className="my-4">
                <h5>Your Profile Information:</h5>
                <ul className="list-unstyled">
                  <li>
                    <strong>Email:</strong> {currentUser?.email}
                  </li>
                  <li>
                    <strong>Email Verified:</strong>{" "}
                    {currentUser?.emailVerified ? (
                      <span className="text-success">✓ Yes</span>
                    ) : (
                      <span className="text-warning">⚠ No</span>
                    )}
                  </li>
                  {/* <li><strong>User ID:</strong> {currentUser?.uid}</li>
                  <li><strong>Provider:</strong> {currentUser?.providerData[0]?.providerId}</li> */}
                </ul>
              </div>

              <div className="my-4">
                <h5>Email Updates</h5>
                {subscriptionLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Form.Check
                      type="switch"
                      id="subscription-toggle"
                      label={subscribed ? "Subscribed to updates" : "Not subscribed"}
                      checked={Boolean(subscribed)}
                      onChange={handleSubscriptionToggle}
                      disabled={subscriptionUpdating}
                    />
                    {manualResubscribeNeeded && (
                      <Alert variant="warning" className="mt-2 py-2">
                        Mailchimp requires you to confirm resubscribing yourself for
                        anti-spam compliance. Please check your email for a
                        confirmation link, or contact us if you don't receive one.
                      </Alert>
                    )}
                  </>
                )}
              </div>

              {currentUser?.photoURL && (
                <div className="my-3">
                  <img
                    src={currentUser.photoURL || RavenHead}
                    alt="Profile"
                    className="rounded-circle"
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                    }}
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = RavenHead;
                    }}
                  />
                </div>
              )}

              <div className="alert alert-info" role="alert">
                <p>
                  <strong>🎉 Success!</strong> Welcome to the members dashboard.
                </p>
                <p>
                  <strong>Currently Available:</strong>
                  <br />
                  <div>
                    <div>
                      <a href="./onomasticon" className="d-inline-block my-2">
                        <img
                          src={onomasticonLogo}
                          alt="Onomasticon - A Book of Names for Foundry VTT"
                          style={{ maxHeight: "50px", width: "auto" }}
                        />
                      </a>
                    </div>
                    <div>
                      Onomasticon is a Foundry VTT module that adds a name
                      generator to your game.
                    </div>
                  </div>
                  <br />
                  💀{" "}
                  <a href="./forbidden-lands-campaign-maps">
                    The Forbidden Lands Maps (super-high resolution with hex
                    number overlays)
                  </a>{" "}
                  💀
                </p>
                <p>
                  Soon we will have free, members-only gaming materials
                  available such as:
                  <br />
                  <em>The Districts of Doskvol for Blades in the Dark</em>.
                </p>
                <p>
                  We will notify you when more free, members-only gaming
                  materials!
                </p>
              </div>

              <div className="d-flex gap-2">
                <Button variant="primary" onClick={() => navigate("/")}>
                  Go to Home
                </Button>
                <Button variant="outline-danger" onClick={handleLogout}>
                  Sign Out
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
